import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  ButtonText,
  ButtonIcon,
  Badge,
  BadgeText,
  Pressable,
  Icon,
} from '@gluestack-ui/themed';
import {
  Plus, ChevronUp, ChevronDown, Trash2, Inbox,
} from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  useCreateMcqQuestion,
  useDeleteMcqQuestion,
} from '@/common/api/endpoints/mcq/mcq';
import { getGetTasksByModuleVersionQueryKey } from '@/common/api/endpoints/tasks/tasks';
import { useDialog } from '@/common/context/DialogContext';

interface MCQOption {
  id: string;
  text: string;
  isCorrect: boolean;
}

interface MCQQuestion {
  id: string;
  text: string;
  explanation?: string;
  difficulty?: 'easy' | 'medium' | 'hard';
  options: MCQOption[];
}

interface QuestionListProps {
  moduleId: string;
  version: string;
  taskId: string;
  questions: MCQQuestion[];
  onQuestionClick: (questionId: string) => void;
  isReadOnly: boolean;
}

const DifficultyBadge: React.FC<{ difficulty?: 'easy' | 'medium' | 'hard' }> = ({ difficulty = 'medium' }) => (
  <Badge action={difficulty === 'easy' ? 'success' : difficulty === 'medium' ? 'warning' : 'error'} size="sm">
    <BadgeText>{difficulty}</BadgeText>
  </Badge>
);

export const QuestionList: React.FC<QuestionListProps> = ({
  moduleId,
  version,
  taskId,
  questions,
  onQuestionClick,
  isReadOnly,
}) => {
  const { t } = useTranslation();
  const { showDialog, showToastMessage } = useDialog();
  const queryClient = useQueryClient();

  // API hooks
  const createQuestion = useCreateMcqQuestion();
  const deleteQuestion = useDeleteMcqQuestion();

  const invalidateTaskQueries = async () => {
    await queryClient.invalidateQueries({ queryKey: getGetTasksByModuleVersionQueryKey(moduleId, version) });
  };

  // Handlers
  const handleAddQuestion = () => {
    const newQuestion = {
      text: t('lecturer.moduleEditor.taskEditor.mcq.newQuestionText'),
      explanation: '',
      difficulty: 'medium' as const,
      options: [
        { text: t('lecturer.moduleEditor.taskEditor.mcq.optionA'), isCorrect: true, feedback: null },
        { text: t('lecturer.moduleEditor.taskEditor.mcq.optionB'), isCorrect: false, feedback: null },
      ],
    };

    createQuestion.mutate({
      moduleId,
      version,
      taskId,
      data: newQuestion,
    }, {
      onSuccess: async () => {
        await invalidateTaskQueries();
        showToastMessage('success', t('lecturer.moduleEditor.taskEditor.mcq.messages.questionAdded'));
      },
      onError: () => {
        showToastMessage('error', t('lecturer.moduleEditor.taskEditor.mcq.messages.questionAddFailed'));
      },
    });
  };

  const handleDeleteQuestion = (questionId: string) => {
    showDialog({
      title: t('lecturer.moduleEditor.taskEditor.mcq.deleteQuestionTitle'),
      message: t('lecturer.moduleEditor.taskEditor.mcq.deleteQuestionMessage'),
      buttons: [
        {
          text: t('common.buttons.cancel'),
          variant: 'outline',
          onPress: () => {},
        },
        {
          text: t('common.buttons.delete'),
          action: 'negative',
          onPress: () => {
            deleteQuestion.mutate({
              moduleId,
              version,
              taskId,
              questionId,
            }, {
              onSuccess: async () => {
                await invalidateTaskQueries();
                showToastMessage('success', t('lecturer.moduleEditor.taskEditor.mcq.messages.questionDeleted'));
              },
              onError: () => {
                showToastMessage('error', t('lecturer.moduleEditor.taskEditor.mcq.messages.questionDeleteFailed'));
              },
            });
          },
        },
      ],
    });
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    // In a real implementation, this would call an API to update sort order
    // For now, we'll just show a toast
    showToastMessage('info', t('lecturer.moduleEditor.taskEditor.mcq.messages.questionMoved'));
  };

  const handleMoveDown = (index: number) => {
    if (index === questions.length - 1) return;
    // In a real implementation, this would call an API to update sort order
    // For now, we'll just show a toast
    showToastMessage('info', t('lecturer.moduleEditor.taskEditor.mcq.messages.questionMoved'));
  };

  // Empty state
  if (questions.length === 0) {
    return (
      <Box py="$8" alignItems="center" justifyContent="center">
        <Icon as={Inbox} size="xl" color="$textHint" mb="$3" />
        <Text size="md" color="$textSubtle" mb="$2">
          {t('lecturer.moduleEditor.taskEditor.mcq.noQuestions')}
        </Text>
        <Text size="sm" color="$textHint" textAlign="center" mb="$4">
          {t('lecturer.moduleEditor.taskEditor.mcq.addFirstQuestion')}
        </Text>
        {!isReadOnly && (
          <Button
            onPress={handleAddQuestion}
            isDisabled={createQuestion.isPending}
            action="primary"
            size="sm"
          >
            <ButtonIcon as={Plus} />
            <ButtonText>
              {createQuestion.isPending ? t('lecturer.moduleEditor.taskEditor.mcq.adding') : t('lecturer.moduleEditor.taskEditor.mcq.addQuestion')}
            </ButtonText>
          </Button>
        )}
      </Box>
    );
  }

  return (
    <VStack space="md">
      {/* Add Question Button */}
      {!isReadOnly && (
        <Button
          onPress={handleAddQuestion}
          isDisabled={createQuestion.isPending}
          action="secondary"
          size="sm"
          alignSelf="flex-start"
        >
          <ButtonIcon as={Plus} />
          <ButtonText>
            {createQuestion.isPending ? t('lecturer.moduleEditor.taskEditor.mcq.adding') : t('lecturer.moduleEditor.taskEditor.mcq.addQuestion')}
          </ButtonText>
        </Button>
      )}

      {/* Questions List */}
      {questions.map((question, index) => (
        <Pressable
          key={question.id}
          onPress={() => !isReadOnly && onQuestionClick(question.id)}
          disabled={isReadOnly}
        >
          <Box
            borderWidth={1}
            borderColor="$borderLight"
            borderRadius="$lg"
            p="$3"
            bg="$bgCard"
            sx={{
              _web: {
                cursor: isReadOnly ? 'default' : 'pointer',
                ':hover': isReadOnly ? {} : {
                  bg: '$bgHover',
                  borderColor: '$borderBrand',
                },
              },
            }}
          >
            <HStack space="md" alignItems="center">
              {/* Question Number Badge */}
              <Badge size="md" variant="solid" action="info" borderRadius="$full" w={28} h={28} justifyContent="center" alignItems="center" p={0}>
                <BadgeText>{index + 1}</BadgeText>
              </Badge>

              {/* Question Text - Flexible width */}
              <Text size="sm" color="$textBody" numberOfLines={1} flex={1} fontWeight="medium">
                {question.text}
              </Text>

              {/* Metadata Badges */}
              <HStack space="sm" alignItems="center">
                <DifficultyBadge difficulty={question.difficulty} />
                <Text size="xs" color="$textSubtle">
                  {t('lecturer.moduleEditor.taskEditor.mcq.optionsCount', { count: question.options.length })}
                </Text>
              </HStack>

              {/* Action Buttons */}
              {!isReadOnly && (
                <HStack space="xs" ml="$2">
                  <Button
                    testID="move-up-button"
                    size="xs"
                    variant="link"
                    onPress={() => handleMoveUp(index)}
                    isDisabled={index === 0}
                    p="$1"
                  >
                    <ButtonIcon as={ChevronUp} size="sm" color="$textSubtle" />
                  </Button>
                  <Button
                    testID="move-down-button"
                    size="xs"
                    variant="link"
                    onPress={() => handleMoveDown(index)}
                    isDisabled={index === questions.length - 1}
                    p="$1"
                  >
                    <ButtonIcon as={ChevronDown} size="sm" color="$textSubtle" />
                  </Button>
                  <Button
                    testID="delete-question-button"
                    size="xs"
                    action="negative"
                    variant="link"
                    onPress={() => handleDeleteQuestion(question.id)}
                    p="$1"
                  >
                    <ButtonIcon as={Trash2} size="sm" color="$statusError" />
                  </Button>
                </HStack>
              )}
            </HStack>
          </Box>
        </Pressable>
      ))}
    </VStack>
  );
};
